<script lang="ts">
	//Hooks
	import { beforeUpdate } from 'svelte';

	//Services
	import { format } from 'svelte-i18n';
	import { get_local_status } from 'src/services/local_requests';

	//Stores
	import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
	import {
		createMemberSettingsSnapshot,
		createMemberStatusSnapshot,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { groups } from 'src/stores/lists/groups';

	//Helpers
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		get_mode,
		is_gen_2,
		isRgbwwPm,
		isXMODDevice,
		real_id,
	} from '../../../helpers/interface_helpers';
	import {
		localGroupRequest,
		handleCloudLocalLists,
		updateGroupPosition,
		cloudGroupRequestV2,
		getDevicesFormatted,
	} from './control_group_helpers';
	import { capFirstChar, colorLimited } from '../../../helpers/utilities';
	import { update_settings } from 'src/helpers/cloud_disabled_helpers';
	import isDimmerWithCalibration from 'src/helpers/is_calibratable_dimmer';

	//Configs
	import { devices_config } from '@devices/index';

	//Design system components
	import { Slider, SliderV2, toast } from '@Shelly/shelly-design-system';

	//Types
	import type { Device } from 'src/types/Device';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let lights_category_devices = [];

	$: memberStatusStore = createMemberStatusSnapshot(lights_category_devices.map((d) => d.id));
	$: memberSettingsStore = createMemberSettingsSnapshot(lights_category_devices.map((d) => d.id));
	$: memberStatus = $memberStatusStore;
	$: memberSettings = $memberSettingsStore;
	export let type: 'vertical' | 'horizontal' | 'simple' = 'vertical';
	export let groupId: string; //unfortunately we need the group to pass it to the function updateGroupPosition. https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/2490
	export let dashControlWidget = false;
	export let value = 1;

	let local = { gain: {}, brightness: {} },
		cloud = { gain: {}, brightness: {} },
		isLoading = false,
		// value = 1,
		min = 0;

	$: group = $groups[groupId];

	// Disable slider if all devices are uncalibrated dimmers
	$: allUncalibrated = lights_category_devices.every(
		(d) =>
			isDimmerWithCalibration(d?.type) &&
			memberStatus?.[real_id(d?.id)]?.[get_gen_2_component_channel_structure(d)]?.flags?.includes(
				'uncalibrated',
			),
	);
	// Disable slider if all devices are offline
	$: allOffline = lights_category_devices.every((d) => {
		const status = memberStatus?.[real_id(d?.id)];
		return !status?.online;
	});

	// Determine slider value
	$: if (!isLoading && group?.hasOwnProperty('slider')) {
		value = group?.slider;
	}

	beforeUpdate(() => {
		let lists = handleCloudLocalLists(
			Array.from(
				Object.values(lights_category_devices)
					.filter((d: Device) => {
						if (!isDimmerWithCalibration(d?.type)) {
							return true;
						}
						const realId = real_id(d.id);
						const baseDevice = $devices[realId];
						if (!baseDevice) {
							return true;
						}

						const deviceForCheck = {
							...baseDevice,
							channel: d.channel ?? baseDevice.channel,
						};
						const componentPath = get_gen_2_component_channel_structure(deviceForCheck);
						return !memberStatus?.[realId]?.[componentPath]?.flags?.includes('uncalibrated');
					})
					.map((d: Device) => {
						if (
							(isXMODDevice(d?.type) || isRgbwwPm(d?.type)) &&
							typeof d.channel === 'number' &&
							d.channel > 0
						) {
							return `${d.id}_${d.channel}`;
						}
						return d.id;
					}),
			),
		);
		for (const device of lights_category_devices) {
			const realId = real_id(device.id);
			const baseDevice = $devices[realId];
			if (!baseDevice) continue;

			const mode = get_mode(
				baseDevice,
				memberStatus[realId],
				devices_settings.getByRealId(realId),
			);
			const slider = device.slider;

			if (slider !== undefined) {
				if (memberStatus[realId] && memberSettings[realId] && devices_config[baseDevice?.type]) {
					const sliderConfig = devices_config[baseDevice.type]?.modes?.[mode]?.[slider];
					if (sliderConfig && min < sliderConfig) {
						min = sliderConfig;
					}
					const sliderType = devices_config[baseDevice.type]?.modes?.[mode]?.slider || slider;
					if (lists.cloud && Object.keys(lists.cloud).length > 0) {
						cloud[sliderType] = lists.cloud;
					}
					if (lists.local && Object.keys(lists.local).length > 0) {
						local[sliderType] = lists.local;
					}
				}
			}
		}
	});

	const cloudRequest = async (devices, data) => {
		let tmpDevices = getDevicesFormatted(devices, data);

		const res = await cloudGroupRequestV2(`/v2/devices/set/groups`, tmpDevices, data);

		if (typeof res !== 'undefined' && Object.keys(res?.failedCommands || {}).length === 0) {
			await updateGroupPosition(value, group);
			return true;
		} else {
			toast.open({ type: 'danger', title: $format('error_global') });
		}
	};

	const localRequest = async (_devices: any, data: any) => {
		for (const device of _devices) {
			let res;
			const baseDeviceId = real_id(device.id);
			const baseDevice = $devices[baseDeviceId];
			if (!baseDevice) continue;

			const deviceForRequest = {
				...device,
				...baseDevice,
				id: device.id,
				channel: device.channel,
			};

			//device mode returns light, color, white, so it will be enough to handle all lights
			if (deviceForRequest.type !== 'SHRGBW2' && deviceForRequest.mode !== 'color') {
				deviceForRequest.mode = 'light';
			}
			if (is_gen_2(deviceForRequest)) {
				const keyToSend = Object.keys(data)[0];
				const valueToSend =
					deviceForRequest.type === 'SNDC-0D4P10WW' && keyToSend !== 'brightness' //supports plus rgbw only
						? colorLimited(Object.values(data)[0] as number | string, 'to')
						: Object.values(data)[0];
				res = await localGroupRequest(
					deviceForRequest,
					{ [keyToSend]: valueToSend },
					`${get_gen_2_component_from_category(deviceForRequest)}.set`,
				);
			} else {
				res = await localGroupRequest(deviceForRequest, data, 'post');
			}
			if (res.isok) {
				const status_req = await get_local_status(baseDevice, baseDevice.ip);
				let newStatus = {
					...memberStatus[baseDevice.id],
					...status_req,
				};
				devices_status.update_device_status({
					id: baseDevice.id,
					msg: newStatus,
				});
				await update_settings(baseDevice);
			} else {
				toast.open({ type: 'danger', title: $format('error_global') });
			}
		}
	};

	const changeGainBrightness = async () => {
		//ToDo this must be refactored maybe
		isLoading = true;

		if (Object.keys(cloud.gain).length > 0) {
			await cloudRequest(Object.values(cloud.gain), { gain: value });
		}
		if (Object.keys(cloud.brightness).length > 0) {
			await cloudRequest(Object.values(cloud.brightness), {
				brightness: value,
			});
		}

		if (Object.keys(local.gain).length > 0) {
			await localRequest(Object.values(local.gain), { gain: value });
		}
		if (Object.keys(local.brightness).length > 0) {
			await localRequest(Object.values(local.brightness), {
				brightness: value,
			});
		}

		isLoading = false;
	};

	const a11yProps = {
		ariaLabel: $format('label_brightness', { default: 'Brightness' }),
		ariaLabelValue: capFirstChar(
			$format('current_brightness', {
				default: 'Current brightness',
			}) + `: ${value}`,
		),
	};
</script>

{#if dashControlWidget}
	<SliderV2
		type="compact"
		withOverlay={false}
		id="slider-awurjge-dash-widget"
		a11yProps={{
			ariaLabel: 'Slider',
			ariaLabelValue: 'Value of slider',
		}}
		{value}
		disabled={allUncalibrated || allOffline}
		backgroundColor="--color-1-opacity"
		on:value_change={async (e) => {
			value = e.detail.value;
			await changeGainBrightness();
		}}
		{isLoading}
	/>
{:else if $showNewLayout}
	<SliderV2
		{a11yProps}
		id="slider-awurjge"
		class={type === 'vertical' ? 'flex-full-height justify-content-center mb-m' : ' mb-m'}
		title={capFirstChar($format('label_brightness', { default: 'brightness' }))}
		{value}
		{type}
		disabled={allUncalibrated || allOffline}
		on:value_change={async (e) => {
			value = e.detail.value;
			await changeGainBrightness();
		}}
		{isLoading}
	/>
{:else}
	<Slider
		{a11yProps}
		id="slider-awurjge"
		class={type === 'vertical' ? 'flex-full-height mb-m' : ' mb-m'}
		title={capFirstChar($format('label_brightness', { default: 'brightness' }))}
		{value}
		{type}
		disabled={allUncalibrated || allOffline}
		on:value_change={async (e) => {
			value = e.detail.value;
			await changeGainBrightness();
		}}
		{isLoading}
	/>
{/if}
